
import { Player } from '@/types/tournament';
import { assignPlayersToCourts } from '@/utils/tournamentLogic';
import { generateAmericanoPartnerships, assignAmericanoCourts, computeAmericanoRoster, generateTeamAmericanoMatches } from './americanoLogic';
import { ExtendedTournamentState, AmericanoPlayer } from './types';
import { toast } from 'sonner';

export const createStartTournament = (
  setTournamentState: React.Dispatch<React.SetStateAction<ExtendedTournamentState>>,
  setShowLeaderboard: (show: boolean) => void,
  language: string,
  t: (key: string, params?: any) => string
) => {
  return (players: Player[], rounds: number, type: 'mexicano' | 'americano' | 'team_americano' = 'mexicano') => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`Starting ${type} tournament with ${players.length} players`);
    }

    // Prevent duplicate "started" toasts firing back-to-back (e.g., StrictMode double invoke)
    const now = Date.now();
    const w = window as any;
    if (!w.__lastStartToast) w.__lastStartToast = { at: 0, key: '' };
    const key = `${type}-${players.length}`;
    if (now - w.__lastStartToast.at < 1000 && w.__lastStartToast.key === key) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Skipping duplicate start toast for', key);
      }
    } else {
      w.__lastStartToast = { at: now, key };
    }

    if (type === 'americano') {
      // Americano tournament logic
      const americanoPlayers: AmericanoPlayer[] = players.map(player => ({
        ...player,
        points: 0,
        gamesPlayed: 0,
        partnerships: [],
        roundScores: [],
        joinedAt: new Date()
      }));

      // Compute playing roster and bench for odd multiples
      const { playing, updated, bench } = computeAmericanoRoster(americanoPlayers);
      const partnerships = generateAmericanoPartnerships(playing, 1);
      const matches = assignAmericanoCourts(partnerships);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Americano tournament started:', { players: americanoPlayers.length, matches: matches.length });
      }

      const computedTotal = bench.length > 0 ? americanoPlayers.length : Math.max(1, americanoPlayers.length - 1);
      setTournamentState({
        tournamentType: 'americano',
        americanoPlayers: updated,
        americanoMatches: matches,
        americanoRoundResults: [],
        isActive: true,
        currentRound: 1,
        totalRounds: computedTotal,
        isRoundActive: true,
        isFinished: false,
        players: [],
        courts: [],
        results: [],
        roundCourts: [],
        roundAmericanoMatches: [{ matches, results: [] }]
      });

      setShowLeaderboard(false);
      if (now === (window as any).__lastStartToast.at) {
        toast.success(language === 'no' ? 'Americano turnering startet!' : 'Americano tournament started!');
      }
      return;
    }

    if (type === 'team_americano') {
      // Team Americano requires an even number of players (teams of 2)
      if (players.length % 2 !== 0) {
        toast.error(language === 'no' 
          ? 'Team Americano krever et partall spillere (lag på 2)' 
          : 'Team Americano requires an even number of players (teams of 2)');
        return;
      }

      // Team Americano: ALL players play simultaneously (no benching)
      const americanoPlayers: AmericanoPlayer[] = players.map(player => ({
        ...player,
        points: 0,
        gamesPlayed: 0,
        partnerships: [],
        roundScores: [],
        isOnBench: false,
        joinedAt: new Date()
      }));

      // Generate FIXED teams (shuffle players first for randomness)
      const shuffledPlayers = [...americanoPlayers].sort(() => Math.random() - 0.5);
      const fixedTeams: Array<{ player1: AmericanoPlayer; player2: AmericanoPlayer }> = [];
      
      for (let i = 0; i < shuffledPlayers.length; i += 2) {
        fixedTeams.push({
          player1: shuffledPlayers[i],
          player2: shuffledPlayers[i + 1]
        });
      }
      
      console.log('Fixed teams created:', fixedTeams.map(t => `${t.player1.name} & ${t.player2.name}`));

      // Generate first round matches with fixed teams
      const matches = assignAmericanoCourts(fixedTeams);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Team Americano tournament started:', { players: americanoPlayers.length, teams: fixedTeams.length, matches: matches.length });
      }

      // Total rounds = (number of teams - 1) since each team plays every other team once
      const computedTotalTeam = Math.max(1, fixedTeams.length - 1);
      setTournamentState({
        tournamentType: 'team_americano',
        americanoPlayers: americanoPlayers,
        americanoMatches: matches,
        americanoRoundResults: [],
        isActive: true,
        currentRound: 1,
        totalRounds: computedTotalTeam,
        isRoundActive: true,
        isFinished: false,
        players: [],
        courts: [],
        results: [],
        roundCourts: [],
        roundAmericanoMatches: [{ matches, results: [] }],
        teamAmericanoFixedTeams: fixedTeams  // Store fixed teams!
      });

      setShowLeaderboard(false);
      if (now === (window as any).__lastStartToast.at) {
        toast.success(language === 'no' ? 'Team Americano turnering startet!' : 'Team Americano tournament started!');
      }
      return;
    }

    // Mexicano tournament logic
    const playersWithRoundScores = players.map(player => ({
      ...player,
      roundScores: [],
      matchesPlayed: 0,  // Explicitly initialize matchesPlayed for bench rotation
      joinedAt: new Date()
    }));

    // Determine courts dynamically from player count: no limit
    const courtsCount = Math.max(1, Math.floor(playersWithRoundScores.length / 4));
    const { courts, updatedPlayers } = assignPlayersToCourts(playersWithRoundScores, courtsCount);
    
    // updatedPlayers already has correct bench flags from assignPlayersToCourts
    const playersWithBenchFlags = updatedPlayers;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Mexicano tournament started:', { players: playersWithBenchFlags.length, courts: courts.length });
      console.log('Setting Mexicano tournament state:', {
        players: playersWithBenchFlags,
        courts,
        results: [],
        currentRound: 1,
        totalRounds: 999, // Unlimited rounds - manually finish only
        isActive: true,
        tournamentType: 'mexicano',
        americanoPlayers: [],
        americanoMatches: [],
        americanoRoundResults: [],
        isRoundActive: false,
        isFinished: false,
        roundCourts: [{ courts, results: [] }]
      });
    }

    setTournamentState({
      players: playersWithBenchFlags,
      courts,
      results: [],
      currentRound: 1,
      totalRounds: 999, // Unlimited rounds - manually finish only
      isActive: true,
      tournamentType: 'mexicano',
      americanoPlayers: [],
      americanoMatches: [],
      americanoRoundResults: [],
      isRoundActive: true,
      isFinished: false,
      roundCourts: [{ courts, results: [] }],
      roundAmericanoMatches: []
    });

    setShowLeaderboard(false);
    
    const message = language === 'no' 
      ? `Mexicano turnering startet med ${playersWithRoundScores.length} spillere!`
      : `Mexicano tournament started with ${playersWithRoundScores.length} players!`;
    
    if (now === (window as any).__lastStartToast.at) {
      toast.success(message);
    }
  };
};
